import React, { useState, useEffect } from 'react';
import { useDatabase } from '../../contexts/DatabaseContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import schema from './schema';
import validator from './validator';
import defaultData from './defaultData';
import { evaluateAppliance } from '../../utils/electrical/patEvaluator.js';

const { FiPlus, FiTrash2, FiSave, FiFileText } = FiIcons;

const PortableApplianceTestingForm = ({ formInstanceId, onSave, onGeneratePDF }) => {
  const { db } = useDatabase();
  const [formData, setFormData] = useState(defaultData);
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (formInstanceId) loadFormData(formInstanceId);
  }, [formInstanceId]);

  const loadFormData = async (instanceId) => {
    try {
      const form = await db.forms.get(parseInt(instanceId));
      if (form && form.data) setFormData(form.data);
    } catch (error) {
      console.error('Error loading form data:', error);
    }
  };

  const handleFieldChange = (section, field, value, index = null) => {
    setFormData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (index !== null) {
        if (!newData[section]) newData[section] = [];
        if (!newData[section][index]) newData[section][index] = {};
        newData[section][index][field] = value;
      } else {
        if (!newData[section]) newData[section] = {};
        newData[section][field] = value;
      }
      return newData;
    });
    setErrors(prev => { const n = { ...prev }; delete n[field]; delete n[`${section}_${field}`]; return n; });
    setWarnings(prev => { const n = { ...prev }; delete n[field]; delete n[`${section}_${field}`]; return n; });
  };

  // Auto-fill test result based on live measurements using the evaluator
  const handleApplianceChange = (index, field, value) => {
    handleFieldChange('appliances', field, value, index);
    const appliances = formData.appliances || [];
    const appliance = { ...appliances[index] };
    appliance[field] = value;

    const { status, failures } = evaluateAppliance(appliance);
    if (status !== 'NOT TESTED') {
      handleFieldChange('appliances', 'testResult', status, index);
      if (failures.length > 0) {
        setWarnings(prev => ({
          ...prev,
          [`appliance_${index}_testResult`]: failures.join('; ')
        }));
      }
    }
  };

  const addRepeater = (sectionId) => {
    setFormData(prev => {
      const section = schema.sections.find(s => s.id === sectionId);
      let blank = {};
      section.fields.forEach(f => { blank[f.id] = ''; });
      return { ...prev, [sectionId]: [...(prev[sectionId] || []), blank] };
    });
  };

  const removeRepeater = (sectionId, index) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: prev[sectionId].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validation = validator.validate(formData);
      setErrors(validation.errors || {});
      setWarnings(validation.warnings || {});

      if (!validation.isValid) { setSaving(false); return; }

      const formRecord = {
        formId: 'portable-appliance-testing',
        instanceId: formInstanceId || `pat-${Date.now()}`,
        data: formData,
        status: 'draft',
        updatedAt: new Date(),
        lastAccessedAt: new Date()
      };

      if (formInstanceId) {
        await db.forms.update(parseInt(formInstanceId), formRecord);
      } else {
        formRecord.createdAt = new Date();
        formRecord.id = await db.forms.add(formRecord);
      }
      if (onSave) onSave(formRecord);
    } catch (error) {
      console.error('Error saving form:', error);
    }
    setSaving(false);
  };

  const renderField = (field, sectionId, value, onChange, error, warning) => {
    const fieldId = `${sectionId}_${field.id}`;
    const err = errors[fieldId] || errors[field.id] || error;
    const warn = warnings[fieldId] || warnings[field.id] || warning;

    switch (field.type) {
      case 'signature':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">{field.label}</label>
            <input type="text" id={fieldId} className="form-input" value={value || ''}
              onChange={(e) => onChange(e.target.value)} placeholder="Signature" />
          </div>
        );
      case 'number':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}{field.required && <span className="required-indicator">*</span>}
            </label>
            <input type="number" id={fieldId} step={field.step || 'any'} className={`form-input ${err ? 'border-red' : ''}`}
              value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />
            {err && <span className="error-message">{err}</span>}
            {warn && <span className="warning-message">{warn}</span>}
          </div>
        );
      case 'textarea':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}{field.required && <span className="required-indicator">*</span>}
            </label>
            <textarea id={fieldId} rows={2} className={`form-input ${err ? 'border-red' : ''}`}
              value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />
            {err && <span className="error-message">{err}</span>}
          </div>
        );
      case 'select': {
        const options = field.options || [];
        const isObjectOptions = options.length > 0 && typeof options[0] === 'object';
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}{field.required && <span className="required-indicator">*</span>}
            </label>
            <select id={fieldId} className={`form-input ${err ? 'border-red' : ''}`}
              value={value || ''} onChange={(e) => onChange(e.target.value)}>
              <option value="">Select {field.label}</option>
              {isObjectOptions
                ? options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)
                : options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            {err && <span className="error-message">{err}</span>}
            {warn && <span className="warning-message">{warn}</span>}
          </div>
        );
      }
      default:
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}{field.required && <span className="required-indicator">*</span>}
            </label>
            <input type={field.type || 'text'} id={fieldId} className={`form-input ${err ? 'border-red' : ''}`}
              value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />
            {err && <span className="error-message">{err}</span>}
          </div>
        );
    }
  };

  const repeaterTitle = (sectionId, index) => {
    if (sectionId === 'appliances') return `Appliance ${index + 1}`;
    if (sectionId === 'testEquipment') return `Test Equipment ${index + 1}`;
    return `Item ${index + 1}`;
  };

  const addLabel = (sectionId) => {
    if (sectionId === 'appliances') return 'Add Appliance';
    if (sectionId === 'testEquipment') return 'Add Test Equipment';
    return 'Add Item';
  };

  const autoFields = ['applianceClass', 'visualInspection', 'earthContinuity', 'leadResistance', 'insulationResistance', 'touchLeakage'];

  return (
    <div className="pat-form">
      <div className="form-header mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Portable Appliance Testing (PAT) Certificate & Register</h1>
        <p className="text-gray-600">
          IET Code of Practice - measurements auto-evaluate pass/fail by appliance class
        </p>
        {Object.keys(warnings).length > 0 && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4">
            <p className="font-bold text-amber-800">Warnings</p>
            {Object.values(warnings).map((w, i) => <p key={i} className="text-amber-800 text-sm">{w}</p>)}
          </div>
        )}
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        {schema.sections.map(section => (
          <div key={section.id} className="card mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{section.title}</h2>

            {section.repeatable ? (
              <div>
                {formData[section.id].map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 relative">
                    <div className="absolute top-2 right-2">
                      <button type="button" onClick={() => removeRepeater(section.id, index)}
                        className="text-red-500 hover:text-red-700 p-1">
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                    <h3 className="font-medium text-gray-800 mb-3">{repeaterTitle(section.id, index)}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.fields.map(field => {
                        const isAuto = section.id === 'appliances' && autoFields.includes(field.id);
                        const onChange = isAuto
                          ? (value) => handleApplianceChange(index, field.id, value)
                          : (value) => handleFieldChange(section.id, field.id, value, index);
                        return renderField(field, `${section.id}_${index}`, item[field.id], onChange);
                      })}
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => addRepeater(section.id)} className="btn btn-secondary">
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  {addLabel(section.id)}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map(field =>
                  renderField(field, section.id, formData[section.id]?.[field.id],
                    (value) => handleFieldChange(section.id, field.id, value))
                )}
              </div>
            )}
          </div>
        ))}

        <div className="flex gap-4 pt-6">
          <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Form'}
          </button>
          <button type="button" onClick={() => onGeneratePDF && onGeneratePDF(formData)} className="btn btn-secondary">
            <SafeIcon icon={FiFileText} className="w-4 h-4" />
            Generate PDF
          </button>
        </div>
      </form>
    </div>
  );
};

export default PortableApplianceTestingForm;